import { useAtomValue } from 'jotai'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  adoptStoredAppSession,
  useAuthenticationState,
} from '~/atoms/AuthenticationState'
import {
  registerSessionExpiredDialog,
  sessionExpiredAtom,
  useSetSessionExpired,
} from '~/atoms/SessionExpiredState'
import { StorageKeys } from '~/atoms/StorageKeys'
import { MangoButton } from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import { DialogDescription } from '~/components/ui/dialog'
import { Size } from '~/types/ThemeTypes'

/**
 * Tells the user their session expired before signing them out, instead of
 * reloading to the login page without warning.
 */
export const SessionExpiredModal = () => {
  const { t } = useTranslation()
  const isSessionExpired = useAtomValue(sessionExpiredAtom)
  const { logout, isUserLoggedIn } = useAuthenticationState()

  const setSessionExpired = useSetSessionExpired()

  useEffect(() => registerSessionExpiredDialog(), [])

  // Another window may finish a refresh after this one gave up on the same
  // one-time token; its new pair resumes this session.
  useEffect(() => {
    if (!isSessionExpired) return
    const onStorage = (event: StorageEvent) => {
      if (event.key === StorageKeys.AuthData && adoptStoredAppSession()) {
        setSessionExpired(false)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [isSessionExpired, setSessionExpired])

  const handleSignInAgain = () => {
    if (isUserLoggedIn) {
      logout()
    } else {
      window.location.reload()
    }
  }

  return (
    <MangoModal
      open={isSessionExpired}
      role="alertdialog"
      size={Size.sm}
      closeButton={false}
      closeOnEscape={false}
      closeOnInteractOutside={false}
      header={t('sessionExpiredModal.title', 'Your session has expired')}
      footer={
        <MangoButton onClick={handleSignInAgain}>
          {t('sessionExpiredModal.signInAgain', 'Sign in again')}
        </MangoButton>
      }
    >
      <DialogDescription color="inherit">
        {t(
          'sessionExpiredModal.message',
          'For your security, you have been signed out. Text message drafts are kept on this device. Sign in again to continue.',
        )}
      </DialogDescription>
    </MangoModal>
  )
}
